import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { useAuth } from '@/context/AuthContext';
import ThemeToggle from '@/components/ThemeToggle';
import { User, Lock, Eye, EyeOff } from '@/components/icons';

export default function Login() {
  const { user, loading, logIn } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && user) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await logIn(email, password);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-bg relative">
      {/* Theme toggle — top-right, above both panels so it's reachable
          regardless of which column the user is focused on. */}
      <div className="absolute top-4 right-4 z-20">
        <ThemeToggle />
      </div>

      {/* ── Left: brand panel ─────────────────────────────────────────── */}
      <BrandPanel />

      {/* ── Right: sign-in form ───────────────────────────────────────── */}
      <section className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[380px]">
          <h1 className="text-[28px] font-bold tracking-tight-plus text-fg text-center">
            Sign in
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <Field
              icon={<User />}
              label="Email"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="email"
              placeholder="name@5cnetwork.com"
              required
              autoFocus
            />
            <PasswordField
              value={password}
              onChange={setPassword}
              show={showPw}
              onToggleShow={() => setShowPw((v) => !v)}
            />

            {error && (
              <div className="text-[12px] text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-md bg-accent-500 text-white text-[14px] font-semibold hover:bg-accent-500/90 disabled:opacity-60 disabled:pointer-events-none transition-colors shadow-sm"
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

// ── Brand panel (left) ────────────────────────────────────────────────

function BrandPanel() {
  return (
    <aside
      className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden text-white"
      style={{
        // Deep neutral base with a subtle diagonal lift. Accent lives in
        // the decorative blobs below, not as the full field.
        background: 'linear-gradient(135deg, #0B0D14 0%, #141829 50%, #1C1F36 100%)',
      }}
    >
      {/* Atmospheric shade — no visible dot edges. Two soft washes that
          drift across the panel; a heavy CSS blur dissolves any circle
          shape into pure color wash. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{ filter: 'blur(120px)' }}
      >
        <div
          className="absolute rounded-full"
          style={{
            top: '-15%', right: '-10%',
            width: '60%', height: '60%',
            background: 'rgb(235 87 87 / 0.35)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            bottom: '-20%', left: '-15%',
            width: '70%', height: '70%',
            background: 'rgb(235 87 87 / 0.22)',
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            top: '30%', left: '20%',
            width: '45%', height: '45%',
            background: 'rgb(120 140 220 / 0.18)',
          }}
        />
      </div>

      {/* logo top-left */}
      <div className="relative z-10 flex items-center gap-2">
        <img src="/logo.png" alt="5C Network" className="h-10 w-auto select-none" draggable={false} />
        <span className="text-[20px] font-semibold tracking-tight-plus leading-none">Network</span>
      </div>

      {/* copy */}
      <div className="relative z-10 max-w-[460px]">
        <h2 className="text-[56px] font-extrabold tracking-tight-plus leading-[1.05]">
          WELCOME
        </h2>
        <p className="mt-3 text-[22px] font-semibold">
          5C Git Leaderboard
        </p>
        <p className="mt-4 text-[14px] leading-relaxed text-white/75">
          Recognition for everyone who ships code across 5C —
          commits, PRs, reviews, and BHAI-driven work surfaced
          side by side. No rankings. Many ways to ship.
        </p>
      </div>

      {/* bottom spacer — keeps the flex layout balanced without extra copy */}
      <div aria-hidden className="h-2" />
    </aside>
  );
}

// ── Form primitives ──────────────────────────────────────────────────

function Field({
  icon, label, type, value, onChange, ...rest
}: {
  icon: React.ReactNode;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <label className="block">
      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-fg-muted">
        {icon} {label}
      </span>
      <input
        {...rest}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full h-11 px-3 rounded-md bg-surface border border-fg/10 text-[14px] text-fg placeholder:text-fg-subtle focus:border-accent-500/50 transition-colors"
      />
    </label>
  );
}

function PasswordField({
  value, onChange, show, onToggleShow,
}: {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggleShow: () => void;
}) {
  return (
    <label className="block">
      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-fg-muted">
        <Lock /> Password
      </span>
      <div className="relative mt-1.5">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="current-password"
          required
          minLength={6}
          className="w-full h-11 pl-3 pr-10 rounded-md bg-surface border border-fg/10 text-[14px] text-fg placeholder:text-fg-subtle focus:border-accent-500/50 transition-colors"
        />
        <button
          type="button"
          onClick={onToggleShow}
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-fg-subtle hover:text-fg transition-colors"
        >
          {show ? <EyeOff /> : <Eye />}
        </button>
      </div>
    </label>
  );
}

// ── Error mapping ────────────────────────────────────────────────────

/**
 * Human-friendly error copy. Never surface raw Firebase codes or
 * "Firebase:" prefixes — the user doesn't care what SDK we use.
 */
function messageFor(e: unknown): string {
  if (e instanceof FirebaseError) {
    switch (e.code) {
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/missing-password':
      case 'auth/missing-email':
        return 'Please enter both your email and password.';
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
        return 'The email or password you entered is incorrect.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact your admin.';
      case 'auth/too-many-requests':
        return 'Too many sign-in attempts. Please wait a few minutes and try again.';
      case 'auth/network-request-failed':
        return 'We couldn\'t reach our servers. Please check your internet connection.';
      case 'auth/operation-not-allowed':
        return 'Sign-in isn\'t available right now. Please contact your admin.';
      default:
        return 'Sorry, we couldn\'t sign you in. Please try again.';
    }
  }
  return 'Sorry, something went wrong. Please try again.';
}
